/**
 * Onboarding API Service
 *
 * Communicates with the Identity service's tenant onboarding endpoints.
 * In dev, Vite proxies /s1 to the gateway. In production, uses the full API URL.
 */

const isDev = import.meta.env.DEV
const API_BASE = import.meta.env.VITE_IDENTITY_API_URL || (isDev ? '/s1' : 'https://api.communitynord.com/s1')

class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

async function request(path, options = {}) {
  const url = `${API_BASE}/v1${path}`

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // Handle no-content responses
  if (response.status === 204) return null

  const data = await response.json().catch(() => null)

  if (!response.ok) {
    const message = data?.error || data?.message || `Request failed (${response.status})`
    throw new ApiError(message, response.status, data)
  }

  return data
}

export const onboardingApi = {
  /**
   * Claim a token with device fingerprint.
   * Returns pre-filled data if valid.
   */
  async claimToken(token, fingerprint) {
    return request(`/tenants/onboard/${token}/claim`, {
      method: 'POST',
      body: JSON.stringify({ fingerprint }),
    })
  },

  /**
   * Complete onboarding — creates tenant + owner user.
   */
  async completeOnboarding(token, data) {
    return request(`/tenants/onboard/${token}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getSubscriptionPlans() {
    return request('/tenants/onboarding/subscription-plans', {
      method: 'GET',
    })
  },

  /**
   * Check if a slug is available (calls the claim endpoint to infer).
   * This is a lightweight client-side helper — real validation happens server-side.
   */
  slugify(text) {
    return text
      .toLowerCase()
      .replace(/[æ]/g, 'ae')
      .replace(/[ø]/g, 'o')
      .replace(/[å]/g, 'a')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50)
  },
}

export { ApiError }